import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Badge } from "@/components/ui/badge";
import NotificationBell from "@/components/NotificationBell";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  BarChart3,
  CalendarDays,
  Users,
  UsersRound,
  TrendingUp,
  Bell,
  Settings,
  Menu,
  LogOut,
  KeyRound,
  ChevronDown,
  Zap,
  Crown,
  MapPin,
  Brain,
  ShoppingCart,
  Package,
  Warehouse,
  Sparkles,
  Sun,
  Moon,
  FileSpreadsheet,
  FileText,
  Store,
  MessageSquare,
} from "lucide-react";

const navItems = [
  { path: "/dashboard", label: "AI Dashboard", icon: Brain, roles: ["ceo"] },
  { path: "/copilot", label: "AI Copilot", icon: Sparkles, roles: ["ceo"] },
  { path: "/", label: "Lịch sân", icon: CalendarDays, roles: ["ceo", "staff", "employee"] },
  { path: "/pos", label: "POS Bán hàng", icon: ShoppingCart, roles: ["ceo"] },
  { path: "/kiotviet-ops", label: "KiotViet", icon: Store, roles: ["ceo"] },
  { path: "/kiotviet", label: "Cấu hình KiotViet", icon: Settings, roles: ["ceo"] },
  { path: "/products", label: "Sản phẩm", icon: Package, roles: ["ceo"] },
  { path: "/warehouse", label: "Kho & Tài chính", icon: Warehouse, roles: ["ceo"] },
  { path: "/check-in", label: "Check-in", icon: MapPin, roles: ["ceo"] },
  { path: "/customers", label: "Khách hàng", icon: Users, roles: ["ceo"] },
  { path: "/groups", label: "Quản lý Hội", icon: UsersRound, roles: ["ceo", "staff", "employee"] },
  { path: "/loyalty", label: "Loyalty", icon: Crown, roles: ["ceo"] },
  { path: "/analytics", label: "Thống kê", icon: TrendingUp, roles: ["ceo"] },
  { path: "/accounting", label: "Kế toán", icon: FileSpreadsheet, roles: ["ceo"] },
  { path: "/quotes", label: "Báo giá sân", icon: FileText, roles: ["ceo"] },
  { path: "/discord", label: "Discord", icon: MessageSquare, roles: ["ceo"] },
  { path: "/notifications", label: "Thông báo", icon: Bell, roles: ["ceo"] },
  { path: "/settings", label: "Cài đặt", icon: Settings, roles: ["ceo"] },
  { path: "/dat-san", label: "Đặt sân", icon: CalendarDays, roles: ["customer"] },
  { path: "/tai-khoan", label: "Tài khoản", icon: Settings, roles: ["customer"] },
];

const roleBadgeMap = {
  ceo: { label: "CEO", className: "bg-lime-500/20 text-lime-400 border-lime-500/30" },
  staff: { label: "Staff", className: "bg-sky-500/20 text-sky-400 border-sky-500/30" },
  employee: { label: "Nhân viên", className: "bg-slate-500/20 text-slate-400 border-slate-500/30" },
  customer: { label: "Khách hàng", className: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
};

function SidebarContent({ user }) {
  const filteredItems = navItems.filter((item) => item.roles.includes(user?.role));
  const { primaryColorDark } = useTheme();

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2.5 px-4 py-5 border-b border-[#334155]">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColorDark }}>
          <Zap className="w-5 h-5 text-[#0f172a]" />
        </div>
        <div>
          <h1 className="font-heading text-lg font-bold tracking-tight text-[#F8FAFC]" data-testid="app-logo">
            KARINOX Arena
          </h1>
          <p className="text-xs text-slate-500">Quản lý sân Pickleball</p>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1" data-testid="sidebar-nav">
        {filteredItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === "/"}
            className={({ isActive }) =>
              `sidebar-link ${isActive ? "active" : "text-slate-400"}`
            }
            data-testid={`nav-${item.path === "/" ? "schedule" : item.path.slice(1)}`}
          >
            <item.icon className="w-4.5 h-4.5 shrink-0" />
            <span>{item.label}</span>
          </NavLink>
        ))}
        <div className="pt-4">
          <div className="rounded-lg bg-[#334155]/40 p-2 text-xs text-slate-500">
            <span className="text-slate-400">v3.0</span> · KARINOX Arena
          </div>
        </div>
      </nav>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme, primaryColorDark } = useTheme();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const roleBadge = roleBadgeMap[user?.role] || roleBadgeMap.customer;

  return (
    <div className="flex h-screen bg-[#0f172a]" data-testid="app-layout">
      {/* Desktop Sidebar - only show when logged in */}
      {user && (
        <aside className="hidden md:flex md:w-64 md:flex-col border-r border-[#334155] bg-[#0f172a]/95 backdrop-blur-md shrink-0 h-screen sticky top-0 overflow-y-auto">
          <SidebarContent user={user} />
        </aside>
      )}

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between h-14 px-4 md:px-6 border-b border-[#334155] bg-[#0f172a]/80 backdrop-blur-sm shrink-0" style={{ zIndex: 100 }} data-testid="app-header">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <button className="md:hidden p-1.5 rounded-md hover:bg-[#334155] transition-colors" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5 text-slate-400" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0 bg-[#0f172a] border-[#334155]">
        <SheetTitle className="sr-only">Menu điều hướng</SheetTitle>
                <div className="h-full overflow-y-auto" onClick={() => setSheetOpen(false)}>
                  <SidebarContent user={user} />
                </div>
              </SheetContent>
            </Sheet>
            <h2 className="font-heading text-base font-semibold text-[#F8FAFC] hidden sm:block">
              {navItems.find((n) => {
                const loc = window.location.pathname;
                if (n.path === "/") return loc === "/";
                return loc.startsWith(n.path);
              })?.label || "KARINOX Arena"}
            </h2>
          </div>

          <div className="flex items-center gap-3">
            {!user ? (
              <>
                <button
                  onClick={toggleTheme}
                  className="p-1.5 rounded-lg hover:bg-[#334155] transition-all duration-200"
                  data-testid="theme-toggle"
                  title={theme === "dark" ? "Chuyển sang Light" : "Chuyển sang Dark"}
                >
                  {theme === "dark"
                    ? <Sun className="w-4.5 h-4.5 text-amber-400" />
                    : <Moon className="w-4.5 h-4.5 text-indigo-500" />}
                </button>
                <a href="/login" className="px-3 py-1.5 bg-lime-500 hover:bg-lime-400 text-[#0f172a] text-xs font-semibold rounded-lg transition-colors" data-testid="header-login-btn">
                  Đăng nhập
                </a>
              </>
            ) : (
              <>
                <Badge className={`${roleBadge.className} rounded-full text-xs px-2.5 py-0.5 border`} data-testid="role-badge">
                  {roleBadge.label}
                </Badge>

            <button
              onClick={toggleTheme}
              className="p-1.5 rounded-lg hover:bg-[#334155] transition-all duration-200"
              data-testid="theme-toggle"
              title={theme === "dark" ? "Chuyển sang Light" : "Chuyển sang Dark"}
            >
              {theme === "dark"
                ? <Sun className="w-4.5 h-4.5 text-amber-400" />
                : <Moon className="w-4.5 h-4.5 text-indigo-500" />}
            </button>

            <NotificationBell />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-[#334155] transition-colors" data-testid="user-menu-trigger">
                  <div className="w-7 h-7 rounded-full bg-[#334155] flex items-center justify-center text-xs font-semibold" style={{ color: primaryColorDark }}>
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                  <span className="text-sm text-slate-300 hidden sm:inline">{user?.name}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-[#1e293b] border-[#334155]">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium text-slate-200">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-[#334155]" />
                <DropdownMenuItem
                  onClick={() => setChangePasswordOpen(true)}
                  className="text-slate-300 focus:text-slate-200 focus:bg-[#334155] cursor-pointer"
                  data-testid="open-change-password-btn"
                >
                  <KeyRound className="w-4 h-4 mr-2" />
                  Đổi mật khẩu
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={logout}
                  className="text-red-400 focus:text-red-400 focus:bg-red-500/10 cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Đăng xuất
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
              </>
            )}
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto p-4 md:p-6" data-testid="main-content">
          {children}
        </main>
      </div>
      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </div>
  );
}
